import { useState } from 'react'
import { AlertTriangle, Check, ChevronDown, Download, FileText, Plus, Printer } from 'lucide-react'
import type { Evaluation, RuleEvaluation, RuleStatus, Work } from '../../core/types.ts'
import { Button } from './ui/button.tsx'
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip.tsx'
import { cn, plural } from '../lib/utils.ts'

/**
 * Nejdůležitější prvek aplikace (components.md §2.1).
 *
 * Tři stavy pravidla nesou tři různé akce uživatele, proto se nikdy neliší jen
 * barvou: splněno = ✓ v plném kruhu, chybí = + v dutém kruhu, přebývá =
 * trojúhelník ve čtverečku. Panel je checklist, ne chybová hláška — žádná
 * červená „error" estetika.
 */

type Props = {
  evaluation: Evaluation
  works: Work[]
  total: number
  locked: boolean
  onRemove: (workId: string) => void
  listId: string
}

const STATE_STYLE: Record<RuleStatus, string> = {
  ok: 'border-transparent',
  missing: 'border-[var(--rule-missing-border)] bg-[var(--rule-missing-surface)]',
  excess: 'border-[var(--rule-excess-border)] bg-[var(--rule-excess-surface)]',
}

const STATE_WORD: Record<RuleStatus, string> = { ok: 'splněno', missing: 'nesplněno', excess: 'porušeno' }

function StatusIcon({ status }: { status: RuleStatus }) {
  if (status === 'ok') {
    return (
      <span aria-hidden className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[var(--rule-ok)] text-[var(--rule-ok-foreground)]">
        <Check size={12} strokeWidth={3} />
      </span>
    )
  }
  if (status === 'missing') {
    return (
      <span aria-hidden className="grid h-5 w-5 shrink-0 place-items-center rounded-full border-[1.5px] border-[var(--rule-missing)] text-[var(--rule-missing)]">
        <Plus size={12} strokeWidth={3} />
      </span>
    )
  }
  return (
    <span aria-hidden className="grid h-5 w-5 shrink-0 place-items-center rounded-[var(--radius-sm)] bg-[var(--rule-excess)] text-[var(--rule-excess-foreground)]">
      <AlertTriangle size={12} strokeWidth={2.5} />
    </span>
  )
}

function RuleRow({ rule, works, locked, onRemove }: { rule: RuleEvaluation; works: Work[]; locked: boolean; onRemove: (id: string) => void }) {
  const [open, setOpen] = useState(false)
  const expandable = rule.offendingWorkIds.length > 0
  const offending = works.filter((w) => rule.offendingWorkIds.includes(w.id))

  return (
    <li
      data-state={rule.status}
      className={cn('rounded-[var(--radius-lg)] border px-3 py-2.5', STATE_STYLE[rule.status])}
    >
      <div className="flex items-start gap-2.5">
        <StatusIcon status={rule.status} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[length:var(--text-sm)] leading-tight">{rule.label}</span>
            <span
              className={cn(
                'tabular shrink-0 text-[length:var(--text-xs)] font-semibold',
                rule.status === 'ok' && 'text-[var(--rule-ok)]',
                rule.status === 'missing' && 'text-[var(--rule-missing)]',
                rule.status === 'excess' && 'text-[var(--rule-excess)]',
              )}
            >
              {rule.current} / {rule.required}
              <span className="sr-only"> — {STATE_WORD[rule.status]}</span>
            </span>
          </div>
          {rule.hint && (
            <p
              className={cn(
                'mt-0.5 text-[length:var(--text-xs)]',
                rule.status === 'excess' ? 'text-[var(--rule-excess)]' : 'text-[var(--muted-foreground)]',
              )}
            >
              {rule.hint}
            </p>
          )}

          {expandable && (
            <>
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className="mt-1.5 inline-flex items-center gap-1 rounded-[var(--radius-sm)] text-[length:var(--text-xs)] font-medium text-[var(--rule-excess)] hover:underline"
              >
                <ChevronDown size={13} className={cn('transition-transform', open && 'rotate-180')} aria-hidden />
                {open ? 'Skrýt díla' : `Ukázat ${plural(offending.length, 'dílo', 'díla', 'děl')}`}
              </button>
              {open && (
                <ul className="mt-1.5 space-y-1">
                  {offending.map((w) => (
                    <li key={w.id} className="flex items-center justify-between gap-2 rounded-[var(--radius-sm)] bg-[var(--card)]/70 px-2 py-1">
                      <span className="min-w-0 truncate font-serif text-[length:var(--text-sm)]">{w.title}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 shrink-0 px-2 text-[var(--rule-excess)]"
                        aria-disabled={locked || undefined}
                        disabled={locked}
                        onClick={() => onRemove(w.id)}
                      >
                        Odebrat
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </li>
  )
}

export function RulePanel({ evaluation, works, total, locked, onRemove, listId }: Props) {
  const selected = evaluation.rules.find((r) => r.ruleId === 'total')?.current ?? 0
  const target = evaluation.rules.find((r) => r.ruleId === 'total')?.required ?? total
  const met = evaluation.rules.filter((r) => r.status === 'ok').length

  // Pod prahem `editor` jde panel nad tabulku: postup a export musí být vidět
  // dřív než 108 řádků kánonu.
  return (
    <aside
      aria-labelledby="rule-panel-title"
      data-complete={evaluation.valid || undefined}
      className="order-first w-full shrink-0 space-y-3 editor:order-none editor:sticky editor:top-4 editor:max-h-[calc(100dvh-2rem)] editor:w-[var(--panel-width)] editor:overflow-y-auto"
    >
      <div
        className={cn(
          'rounded-[var(--radius-xl)] border p-[var(--space-4)]',
          evaluation.valid
            ? 'border-[var(--rule-ok-border)] bg-[var(--rule-ok-surface)]'
            : 'border-[var(--border)] bg-[var(--card)]',
        )}
      >
        <div className="flex items-baseline justify-between">
          <h2 id="rule-panel-title" className="text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-label)] text-[var(--muted-foreground)]">
            Kontrola pravidel
          </h2>
          <span className="tabular text-[length:var(--text-xs)] text-[var(--muted-foreground)]">
            Pravidla {met} z {evaluation.rules.length}
          </span>
        </div>

        <div className="mt-1 flex items-baseline gap-2">
          <span className={cn('tabular text-[length:var(--text-3xl)] font-bold leading-none tracking-[var(--tracking-tight)]', evaluation.valid && 'text-[var(--rule-ok)]')}>
            {selected}
          </span>
          <span className="text-[length:var(--text-sm)] text-[var(--muted-foreground)]">z {target} děl</span>
        </div>

        <div role="img" aria-label={`${selected} z ${target} děl vybráno`} className="mt-3 flex gap-[3px]">
          {Array.from({ length: target }, (_, i) => (
            <span
              key={i}
              className={cn(
                'h-1.5 flex-1 rounded-full',
                i < selected
                  ? evaluation.valid
                    ? 'bg-[var(--rule-ok)]'
                    : 'bg-[var(--work-selected)]'
                  : 'bg-[var(--border)]',
              )}
            />
          ))}
        </div>

        <p aria-live="polite" className="sr-only">
          {selected} z {target} děl, {evaluation.rules.length - met} pravidel nesplněných.
        </p>
      </div>

      {/* Na úzkém okně je devět pravidel dlouhá zeď mezi souhrnem a seznamem,
          tak se schovají pod proužek. Od `editor` je panel vlastní sloupec
          a rozpis je vidět rovnou. */}
      <details className="group editor:hidden" open={!evaluation.valid && met === 0}>
        <summary className="flex cursor-pointer list-none items-center justify-between rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--card)] px-3 py-2.5 text-[length:var(--text-sm)]">
          <span className="font-medium">Rozpis pravidel</span>
          <span className="flex items-center gap-1.5 text-[var(--muted-foreground)]">
            <span className="tabular text-[length:var(--text-xs)]">{met} z {evaluation.rules.length}</span>
            <ChevronDown size={15} aria-hidden className="transition-transform group-open:rotate-180" />
          </span>
        </summary>
        <ul className="mt-1.5 space-y-1.5">
          {evaluation.rules.map((rule) => (
            <RuleRow key={rule.ruleId} rule={rule} works={works} locked={locked} onRemove={onRemove} />
          ))}
        </ul>
      </details>

      <ul className="hidden space-y-1.5 editor:block">
        {evaluation.rules.map((rule) => (
          <RuleRow key={rule.ruleId} rule={rule} works={works} locked={locked} onRemove={onRemove} />
        ))}
      </ul>

      <div className="flex flex-col gap-2 no-print">
        {evaluation.valid ? (
          <Button asChild className="bg-[var(--rule-ok)] text-[var(--rule-ok-foreground)]">
            <a href={`/api/lists/${listId}/export.docx`}>
              <Download size={15} aria-hidden /> Stáhnout DOCX
            </a>
          </Button>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button aria-disabled className="cursor-not-allowed bg-[var(--rule-ok)] text-[var(--rule-ok-foreground)] opacity-45" onClick={(e) => e.preventDefault()}>
                <Download size={15} aria-hidden /> Stáhnout DOCX
              </Button>
            </TooltipTrigger>
            <TooltipContent>Export se odemkne, až budou splněná všechna pravidla.</TooltipContent>
          </Tooltip>
        )}
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" asChild>
            <a href={`/lists/${listId}/print`}>
              <Printer size={15} aria-hidden /> Tisk
            </a>
          </Button>
          <Button variant="outline" className="flex-1" asChild>
            <a href={`/api/lists/${listId}/export.csv`}>
              <FileText size={15} aria-hidden /> CSV
            </a>
          </Button>
        </div>
      </div>
    </aside>
  )
}
